import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchBuyerBookings,
  type Booking,
  type Review,
  fetchReviewByBooking,
} from '../../features/marketplace/api';

export default function BuyerProfilePage() {
  const { buyerAlias } = useParams<{ buyerAlias: string }>();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (buyerAlias) {
      loadBuyerProfile();
    }
  }, [buyerAlias]);

  async function loadBuyerProfile() {
    if (!buyerAlias) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch all bookings for this buyer
      const bookingsData = await fetchBuyerBookings(buyerAlias);
      setBookings(bookingsData || []);

      // Fetch seller reviews for completed bookings (reviews sellers left about this buyer)
      const reviewsList: Review[] = [];
      for (const booking of bookingsData || []) {
        if (booking.status === 'completed' || booking.status === 'reviewed') {
          try {
            // Fetch seller-to-buyer review
            const sellerReview = await fetchReviewByBooking(booking.id, 'seller_to_buyer');
            if (sellerReview) {
              reviewsList.push(sellerReview);
            }
          } catch (err) {
            // Review might not exist, that's okay
          }
        }
      }
      setReviews(reviewsList);
    } catch (err) {
      console.error('Failed to load buyer profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load buyer profile');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center text-slate-600 dark:text-slate-400">Loading buyer profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-6 text-center">
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white rounded transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalBookings = bookings.length;
  const completedBookings = bookings.filter(b => b.status === 'completed' || b.status === 'reviewed').length;
  const cancelledBookings = bookings.filter(b => b.status === 'cancelled').length;
  const noShowBookings = bookings.filter(b => b.status === 'no_show').length;
  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 'N/A';

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white rounded text-sm transition-colors"
      >
        ← Back
      </button>

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Buyer Profile</h1>
        <p className="text-xl text-slate-600 dark:text-slate-400">{buyerAlias}</p>
        <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
          This profile shows booking history and reviews, but does not disclose specific services used.
        </p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{totalBookings}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Total Bookings</div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{completedBookings}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Completed</div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{noShowBookings}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">No-Shows</div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{averageRating}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Avg. Rating Given</div>
        </div>
      </div>

      {/* Booking History (without service details) */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Booking History</h2>
        {bookings.length === 0 ? (
          <p className="text-slate-600 dark:text-slate-400">No bookings yet.</p>
        ) : (
          <div className="space-y-3">
            {bookings.map((booking) => (
              <div key={booking.id} className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="text-sm text-slate-700 dark:text-slate-400">
                      {new Date(booking.scheduledFor).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                      Requested: {new Date(booking.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      booking.status === 'completed' || booking.status === 'reviewed'
                        ? 'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 text-green-700 dark:text-green-400'
                        : booking.status === 'no_show'
                          ? 'bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-800 text-orange-700 dark:text-orange-400'
                          : booking.status === 'cancelled'
                            ? 'bg-slate-200 dark:bg-slate-900/30 border border-slate-300 dark:border-slate-800 text-slate-700 dark:text-slate-400'
                            : 'bg-brand-100 dark:bg-brand-900/30 border border-brand-300 dark:border-brand-800 text-brand-700 dark:text-brand-400'
                    }`}
                  >
                    {booking.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-500">
                  Service details hidden for privacy
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reviews from Sellers */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Reviews from Sellers</h2>
        {reviews.length === 0 ? (
          <p className="text-slate-600 dark:text-slate-400">No reviews yet.</p>
        ) : (
          <div className="space-y-3">
            {reviews.map((review) => (
              <div key={review.id} className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-yellow-600 dark:text-yellow-400 text-lg">
                    {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-500">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-300">{review.comment}</p>
                <div className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                  Service details hidden for privacy
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
